import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { 
  AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, Filter, RefreshCw, Trophy, Sparkles, BookOpen, Mic, FileText, Globe
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { errorReviewService, ExerciseErrorRecord } from '../services/errorReviewService';
import ErrorReviewModal from '../components/ErrorReviewModal';
import { useTranslation } from 'react-i18next';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';

const LANGUAGE_NAMES: Record<string, string> = {
  en: 'Inglês',
  es: 'Espanhol',
  fr: 'Francês',
  de: 'Alemão',
  it: 'Italiano',
  hu: 'Húngaro',
  ru: 'Russo',
  pt: 'Português'
};

const LANGUAGE_FLAGS: Record<string, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  hu: '🇭🇺',
  ru: '🇷🇺',
  pt: '🇧🇷'
};

const PAGE_STRINGS: Record<string, Record<string, string>> = {
  pt: {
    back: 'Voltar ao Hub',
    pageTitle: 'CADERNO DE ERROS',
    pageSubtitle: 'REVISÃO INTELIGENTE E RETENÇÃO ESPAÇADA',
    pendingToday: 'ERROS PENDENTES HOJE',
    pendingDetail: 'Liberados para revisão imediata',
    mastered: 'ITENS DOMINADOS',
    masteredDetail: '3 acertos seguidos alcançados! 🎉',
    filterExercises: 'Filtrar Exercícios',
    language: 'Idioma:',
    allLangs: 'Todos os Idiomas',
    exerciseType: 'Tipo de Exercício:',
    allTypes: 'Todos os Tipos',
    readyToCorrect: 'Pronto para Corrigir seus Erros?',
    pendingCountMessage: 'Você tem {count} exercícios aguardando revisão nesta seleção.',
    startSession: 'Iniciar Sessão de Revisão',
    errorListTitle: 'Lista de Erros Pendentes ({count})',
    loading: 'Carregando caderno de erros...',
    noErrors: 'Nenhum erro pendente para este filtro!',
    noErrorsDetail: 'Parabéns! Todos os seus erros nesta categoria já foram revisados ou estão agendados para os próximos dias.',
    errorsCount: 'Erros: {count}',
    streakCount: 'Acertos seguidos: {count}/3',
    pendingStatus: 'Pendente',
    pagePrev: 'Anterior',
    pageNext: 'Próxima',
    pageOf: 'Página {current} de {total}',
    selectLangTitle: 'Selecione o Idioma para Correção',
    selectLangSubtitle: 'Escolha qual dos seus idiomas em estudo você deseja revisar no Caderno de Erros agora:'
  },
  en: {
    back: 'Back to Hub',
    pageTitle: 'ERROR NOTEBOOK',
    pageSubtitle: 'SMART REVIEW AND SPACED RETENTION',
    pendingToday: 'PENDING ERRORS TODAY',
    pendingDetail: 'Ready for immediate review',
    mastered: 'MASTERED ITEMS',
    masteredDetail: '3 consecutive correct hits! 🎉',
    filterExercises: 'Filter Exercises',
    language: 'Language:',
    allLangs: 'All Languages',
    exerciseType: 'Exercise Type:',
    allTypes: 'All Types',
    readyToCorrect: 'Ready to Fix Your Errors?',
    pendingCountMessage: 'You have {count} exercises waiting for review in this selection.',
    startSession: 'Start Review Session',
    errorListTitle: 'Pending Errors List ({count})',
    loading: 'Loading error notebook...',
    noErrors: 'No pending errors for this filter!',
    noErrorsDetail: 'Congratulations! All errors in this category have been reviewed or scheduled for future dates.',
    errorsCount: 'Errors: {count}',
    streakCount: 'Consecutive correct: {count}/3',
    pendingStatus: 'Pending',
    pagePrev: 'Previous',
    pageNext: 'Next',
    pageOf: 'Page {current} of {total}',
    selectLangTitle: 'Select Language for Correction',
    selectLangSubtitle: 'Choose which of your studied languages you want to review in the Error Notebook now:'
  },
  es: {
    back: 'Volver al Hub',
    pageTitle: 'CUADERNO DE ERRORES',
    pageSubtitle: 'REVISIÓN INTELIGENTE Y RETENCIÓN ESPACIADA',
    pendingToday: 'ERRORES PENDIENTES HOY',
    pendingDetail: 'Listos para revisión inmediata',
    mastered: 'ELEMENTOS DOMINADOS',
    masteredDetail: '¡3 aciertos seguidos logrados! 🎉',
    filterExercises: 'Filtrar Ejercicios',
    language: 'Idioma:',
    allLangs: 'Todos los Idiomas',
    exerciseType: 'Tipo de Ejercicio:',
    allTypes: 'Todos los Tipos',
    readyToCorrect: '¿Listo para Corregir tus Errores?',
    pendingCountMessage: 'Tienes {count} ejercicios esperando revisión en esta selección.',
    startSession: 'Iniciar Sesión de Revisión',
    errorListTitle: 'Lista de Errores Pendientes ({count})',
    loading: 'Cargando cuaderno de errores...',
    noErrors: '¡Sin errores pendientes para este filtro!',
    noErrorsDetail: '¡Felicitaciones! Todos tus errores en esta categoría han sido revisados.',
    errorsCount: 'Errores: {count}',
    streakCount: 'Aciertos seguidos: {count}/3',
    pendingStatus: 'Pendiente',
    pagePrev: 'Anterior',
    pageNext: 'Siguiente',
    pageOf: 'Página {current} de {total}',
    selectLangTitle: 'Selecciona el Idioma para Corrección',
    selectLangSubtitle: 'Elige cuál de tus idiomas en estudio deseas revisar en el Cuaderno de Errores ahora:'
  },
  fr: {
    back: 'Retour au Hub',
    pageTitle: 'CARNET DE FAUTES',
    pageSubtitle: 'RÉVISION INTELLIGENTE ET RÉTENTION ESPACÉE',
    pendingToday: 'ERREURS EN ATTENTE AUJOURD\'HUI',
    pendingDetail: 'Prêtes pour révision immédiate',
    mastered: 'ÉLÉMENTS MAÎTRISÉS',
    masteredDetail: '3 succès consécutifs atteints ! 🎉',
    filterExercises: 'Filtrer les Exercices',
    language: 'Langue :',
    allLangs: 'Toutes les Langues',
    exerciseType: 'Type d\'Exercice :',
    allTypes: 'Tous les Types',
    readyToCorrect: 'Prêt à Corriger vos Erreurs ?',
    pendingCountMessage: 'Vous avez {count} exercices en attente de révision.',
    startSession: 'Démarrer la Session',
    errorListTitle: 'Liste des Erreurs en Attente ({count})',
    loading: 'Chargement du carnet de fautes...',
    noErrors: 'Aucune erreur en attente pour ce filtre !',
    noErrorsDetail: 'Félicitations ! Toutes vos erreurs dans cette catégorie ont été révisées.',
    errorsCount: 'Erreurs : {count}',
    streakCount: 'Succès consécutifs : {count}/3',
    pendingStatus: 'En attente',
    pagePrev: 'Précédent',
    pageNext: 'Suivant',
    pageOf: 'Page {current} sur {total}',
    selectLangTitle: 'Sélectionnez la Langue pour Correction',
    selectLangSubtitle: 'Choisissez laquelle de vos langues d\'étude vous souhaitez réviser maintenant :'
  },
  de: {
    back: 'Zurück zum Hub',
    pageTitle: 'FEHLERHEFT',
    pageSubtitle: 'INTELLIGENTE WIEDERHOLUNG UND GESTAFFELTE ERINNERUNG',
    pendingToday: 'HEUTE ANSTEHENDE FEHLER',
    pendingDetail: 'Bereit zur sofortigen Wiederholung',
    mastered: 'GEMEISTERTE ELEMENTE',
    masteredDetail: '3 Erfolge hintereinander erreicht! 🎉',
    filterExercises: 'Übungen Filtern',
    language: 'Sprache:',
    allLangs: 'Alle Sprachen',
    exerciseType: 'Übungstyp:',
    allTypes: 'Alle Typen',
    readyToCorrect: 'Bereit, Ihre Fehler zu korrigieren?',
    pendingCountMessage: 'Sie haben {count} Übungen zur Überprüfung ausstehend.',
    startSession: 'Sitzung Starten',
    errorListTitle: 'Ausstehende Fehlerliste ({count})',
    loading: 'Fehlerheft wird geladen...',
    noErrors: 'Keine anstehenden Fehler für diesen Filter!',
    noErrorsDetail: 'Herzlichen Glückwunsch! Alle Fehler in dieser Kategorie wurden überprüft.',
    errorsCount: 'Fehler: {count}',
    streakCount: 'Erfolge hintereinander: {count}/3',
    pendingStatus: 'Ausstehend',
    pagePrev: 'Vorherige',
    pageNext: 'Nächste',
    pageOf: 'Seite {current} von {total}',
    selectLangTitle: 'Wählen Sie die Sprache zur Korrektur',
    selectLangSubtitle: 'Wählen Sie die zu überprüfende Sprache aus:'
  },
  it: {
    back: 'Torna all\'Hub',
    pageTitle: 'QUADERNO DEGLI ERRORI',
    pageSubtitle: 'REVISIONE INTELLIGENTE E RITENZIONE DIRETTA',
    pendingToday: 'ERRORI IN SOSPESO OGGI',
    pendingDetail: 'Pronti per la revisione immediata',
    mastered: 'ELEMENTI PADRONEGGIATI',
    masteredDetail: '3 successi consecutivi raggiunti! 🎉',
    filterExercises: 'Filtra Esercizi',
    language: 'Lingua:',
    allLangs: 'Tutte le Lingue',
    exerciseType: 'Tipo di Esercizio:',
    allTypes: 'Tutti i Tipi',
    readyToCorrect: 'Pronto a Correggere i tuoi Errori?',
    pendingCountMessage: 'Hai {count} esercizi in attesa di revisione.',
    startSession: 'Inizia Sessione',
    errorListTitle: 'Elenco Errori in Sospeso ({count})',
    loading: 'Caricamento quaderno degli errori...',
    noErrors: 'Nessun errore in sospeso per questo filtro!',
    noErrorsDetail: 'Congratulazioni! Tutti i tuoi errori in questa categoria sono stati revisionati.',
    errorsCount: 'Errori: {count}',
    streakCount: 'Successi consecutivi: {count}/3',
    pendingStatus: 'In sospeso',
    pagePrev: 'Precedente',
    pageNext: 'Successiva',
    pageOf: 'Pagina {current} di {total}',
    selectLangTitle: 'Seleziona la Lingua per la Correzione',
    selectLangSubtitle: 'Scegli quale delle tue lingue di studio desideri revisionare ora:'
  }
};

export const ReviewErrorsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isGuide = location.pathname.includes('/guide') || searchParams.get('from') === 'guide';
  const paramLang = searchParams.get('lang');

  const { user } = useAuth();
  const { i18n } = useTranslation();
  const nativeLang = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
  const tStr = PAGE_STRINGS[nativeLang] || PAGE_STRINGS.pt;

  const initialLang = (paramLang ? paramLang.toLowerCase().split('-')[0] : null) || 
    localStorage.getItem('libritui_active_study_lang') || '';

  const paramType = searchParams.get('type');

  const [selectedLang, setSelectedLang] = useState<string>(() => initialLang);
  const [showLangModal, setShowLangModal] = useState<boolean>(() => !initialLang);
  const [selectedType, setSelectedType] = useState<string>(() => paramType || 'all');
  const [pendingErrors, setPendingErrors] = useState<ExerciseErrorRecord[]>([]);
  const [stats, setStats] = useState<{ pendingCount: number; masteredCount: number }>({ pendingCount: 0, masteredCount: 0 });
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [langCounts, setLangCounts] = useState<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (paramLang) {
      const clean = paramLang.toLowerCase().split('-')[0];
      setSelectedLang(clean);
      setShowLangModal(false);
    }
    if (paramType) {
      setSelectedType(paramType);
    }
  }, [paramLang, paramType]);

  useEffect(() => {
    if (user && user.id !== 'guest' && showLangModal) {
      const langs = ['en', 'es', 'fr', 'de', 'it', 'hu', 'ru', 'pt'];
      const fetchCountsSequentially = async () => {
        const map: Record<string, number> = {};
        for (const l of langs) {
          try {
            const errs = await errorReviewService.getPendingErrors(user.id, l);
            map[l] = errs.length;
          } catch (e) {
            map[l] = 0;
          }
        }
        setLangCounts(map);
      };
      fetchCountsSequentially();
    }
  }, [user, showLangModal]);

  const loadData = async () => {
    if (!user || user.id === 'guest' || !selectedLang) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setCurrentPage(1);
    try {
      const list = await errorReviewService.getPendingErrors(user.id, selectedLang, selectedType);
      setPendingErrors(list);

      const st = await errorReviewService.getErrorStats(user.id, selectedLang);
      setStats(st);
    } catch (err) {
      console.error('[ReviewErrorsPage] Error loading errors:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLang) {
      loadData();
    }
  }, [user, selectedLang, selectedType]);

  const totalPages = Math.ceil(pendingErrors.length / ITEMS_PER_PAGE) || 1;
  const paginatedErrors = pendingErrors.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-surface text-ink pb-36 pt-6 px-4 sm:px-8 max-w-4xl mx-auto space-y-8 animate-in fade-in duration-300">
      
      {/* Top Header */}
      <div className="space-y-4">
        <div>
          <AppBackButton
            onClick={() => isGuide ? navigate('/') : goBackOr(navigate, '/study-hub')}
            fallbackUrl={isGuide ? '/' : '/study-hub'}
          />
        </div>

        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-bad/20 text-bad flex items-center justify-center shrink-0">
            <AlertCircle size={26} />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium text-ink tracking-[-0.03em]">{tStr.pageTitle}</h1>
            <p className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{tStr.pageSubtitle}</p>
          </div>
        </div>
      </div>

      {/* Metrics Cards Header */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-6 rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-2 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-bad/5 rounded-full blur-2xl pointer-events-none" />
          <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block">{tStr.pendingToday}</span>
          <div className="text-3xl font-medium text-ink">{stats.pendingCount}</div>
          <span className="text-xs text-muted font-medium block">{tStr.pendingDetail}</span>
        </div>

        <div className="p-6 rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-2 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-ok/5 rounded-full blur-2xl pointer-events-none" />
          <span className="text-[10px] font-normal text-ok uppercase font-mono tracking-[0.14em] block">{tStr.mastered}</span>
          <div className="text-3xl font-medium text-ok">{stats.masteredCount}</div>
          <span className="text-xs text-muted font-medium block">{tStr.masteredDetail}</span>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="p-6 rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-4">
        <div className="flex items-center gap-2 text-xs font-normal text-muted uppercase font-mono tracking-[0.14em] mb-2">
          <Filter size={16} className="text-primary" />
          <span>{tStr.filterExercises}</span>
        </div>

        {/* Language selector pills */}
        <div className="space-y-2">
          <span className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] block">{tStr.language}</span>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedLang('all')}
              className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                selectedLang === 'all'
                  ? 'bg-ink text-ink'
                  : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink'
              }`}
            >
              {tStr.allLangs}
            </button>

            {Object.keys(LANGUAGE_NAMES).map((lang) => (
              <button
                key={lang}
                onClick={() => setSelectedLang(lang)}
                className={`px-3 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer ${
                  selectedLang === lang
                    ? 'bg-ink text-ink'
                    : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink'
                }`}
              >
                <span>{LANGUAGE_FLAGS[lang]}</span>
                <span>{LANGUAGE_NAMES[lang]}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Exercise type pills */}
        <div className="space-y-2 pt-2">
          <span className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] block">{tStr.exerciseType}</span>
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'all', label: tStr.allTypes },
              { id: 'quizzes', label: 'Quizzes' },
              { id: 'flashcards', label: 'Flashcards' },
              { id: 'pronunciation', label: 'Pronúncia' },
              { id: 'writing', label: 'Escrita' },
              { id: 'listening', label: 'Listening' }
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setSelectedType(t.id)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  selectedType === t.id
                    ? 'bg-bad text-surface'
                    : 'bg-surface border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Action Banner */}
      {pendingErrors.length > 0 && (
        <div className="p-6 rounded-3xl bg-tint-rose text-ink flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-xl font-semibold">{tStr.readyToCorrect}</h3>
            <p className="text-xs font-medium text-surface">
              {tStr.pendingCountMessage.replace('{count}', pendingErrors.length.toString())}
            </p>
          </div>

          <button
            onClick={() => setShowReviewModal(true)}
            className="px-6 py-4 bg-surface text-ink hover:bg-tint-stone/60 rounded-2xl font-medium text-[13px] transition-all hover:scale-105 cursor-pointer whitespace-nowrap flex items-center gap-2"
          >
            <Sparkles size={18} className="text-bad" />
            <span>{tStr.startSession}</span>
          </button>
        </div>
      )}

      {/* List of Pending Error Items (Paginated to 10 items per page) */}
      <div className="space-y-4">
        <h3 className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]">
          {tStr.errorListTitle.replace('{count}', pendingErrors.length.toString())}
        </h3>

        {loading ? (
          <div className="p-8 text-center text-muted text-xs font-bold animate-pulse">
            {tStr.loading}
          </div>
        ) : pendingErrors.length === 0 ? (
          <div className="p-12 text-center rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] space-y-3">
            <div className="w-12 h-12 bg-ok/20 text-ok rounded-2xl flex items-center justify-center mx-auto">
              <CheckCircle2 size={24} />
            </div>
            <h4 className="text-base font-bold text-ink">{tStr.noErrors}</h4>
            <p className="text-xs text-muted max-w-sm mx-auto">
              {tStr.noErrorsDetail}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {paginatedErrors.map((err) => {
              let payload = err.item_payload_json || {};
              if (typeof payload === 'string') {
                try { payload = JSON.parse(payload); } catch (e) {}
              }
              const titleText = 
                payload.question || 
                payload.front || 
                payload.original || 
                payload.word || 
                payload.term || 
                payload.phrase || 
                payload.prompt || 
                payload.title || 
                payload.text || 
                (payload.card && (payload.card.front || payload.card.original || payload.card.word || payload.card.term)) ||
                (err.item_id && err.item_id.length > 0 && !err.item_id.includes('-') && isNaN(Number(err.item_id)) ? err.item_id : '') ||
                'Exercício';
              const langCode = (err.language || 'en').toLowerCase();
              const flag = LANGUAGE_FLAGS[langCode] || '🌐';

              return (
                <div
                  key={err.id}
                  className="p-5 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] dark:hover:border-white/[0.06] transition-all flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-2xl select-none">{flag}</span>
                    <div className="text-left space-y-1">
                      <span className="font-bold text-sm text-ink block">{titleText}</span>
                      <div className="flex items-center gap-2 text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
                        <span className="px-2 py-0.5 rounded-md bg-tint-stone text-ink">
                          {err.exercise_type}
                        </span>
                        <span>• {tStr.errorsCount.replace('{count}', err.error_count.toString())}</span>
                        <span>• {tStr.streakCount.replace('{count}', err.consecutive_correct.toString())}</span>
                      </div>
                    </div>
                  </div>

                  <span className="text-[10px] font-medium text-warn bg-warn/10 px-3 py-1 rounded-full border border-warn/20">
                    {tStr.pendingStatus}
                  </span>
                </div>
              );
            })}

            {/* Pagination Controls */}
            {pendingErrors.length > ITEMS_PER_PAGE && (
              <div className="flex items-center justify-between pt-6 border-t border-black/[0.06] dark:border-white/[0.06]">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2.5 rounded-xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2 text-xs font-bold cursor-pointer"
                >
                  <ChevronLeft size={16} />
                  <span>{tStr.pagePrev || 'Anterior'}</span>
                </button>

                <span className="text-xs font-bold text-muted">
                  {(tStr.pageOf || 'Página {current} de {total}')
                    .replace('{current}', currentPage.toString())
                    .replace('{total}', totalPages.toString())}
                </span>

                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage >= totalPages}
                  className="px-4 py-2.5 rounded-xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2 text-xs font-bold cursor-pointer"
                >
                  <span>{tStr.pageNext || 'Próxima'}</span>
                  <ChevronRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Initial Language Selection Modal */}
      {(showLangModal || !selectedLang) && (
        <div className="fixed inset-0 z-[9999] bg-black/30 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-3xl p-6 sm:p-8 max-w-md w-full text-center space-y-6 animate-in zoom-in-95 duration-200 relative">
            <div className="w-16 h-16 bg-primary/20 text-primary rounded-2xl flex items-center justify-center mx-auto">
              <Globe size={32} />
            </div>

            <div className="space-y-2">
              <h2 className="text-xl font-medium text-ink tracking-[-0.03em]">
                {tStr.selectLangTitle || 'Selecione o Idioma para Correção'}
              </h2>
              <p className="text-muted text-xs font-semibold leading-relaxed">
                {tStr.selectLangSubtitle || 'Escolha qual dos seus idiomas em estudo você deseja revisar no Caderno de Erros agora:'}
              </p>
            </div>

            <div className="grid grid-cols-1 gap-3 pt-2">
              {[
                { code: 'en', name: 'Inglês', flag: '🇬🇧' },
                { code: 'es', name: 'Espanhol', flag: '🇪🇸' },
                { code: 'fr', name: 'Francês', flag: '🇫🇷' },
                { code: 'de', name: 'Alemão', flag: '🇩🇪' },
                { code: 'it', name: 'Italiano', flag: '🇮🇹' }
              ].map(lang => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setSelectedLang(lang.code);
                    setShowLangModal(false);
                  }}
                  className="w-full py-4 px-6 bg-surface/80 hover:bg-ink hover:text-ink border border-black/[0.06] dark:border-white/[0.06] rounded-2xl font-semibold text-sm transition-all flex items-center justify-between cursor-pointer group"
                >
                  <span className="flex items-center gap-3">
                    <span className="text-xl">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </span>
                  <div className="flex items-center gap-3">
                    {typeof langCounts[lang.code] === 'number' && (
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                        langCounts[lang.code] > 0 
                          ? 'text-bad bg-bad/10 border-bad/30' 
                          : 'text-muted bg-surface/80 border-black/[0.06] dark:border-white/[0.06]'
                      }`}>
                        {langCounts[lang.code]} {langCounts[lang.code] === 1 ? 'erro' : 'erros'}
                      </span>
                    )}
                    <span className="text-xs text-muted group-hover:text-ink uppercase font-mono tracking-[0.14em] font-normal">→</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ErrorReviewModal
          language={selectedLang === 'all' ? 'en' : selectedLang}
          targetLangName={selectedLang === 'all' ? 'Todos os Idiomas' : (LANGUAGE_NAMES[selectedLang] || selectedLang)}
          errors={pendingErrors}
          onClose={() => {
            setShowReviewModal(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default ReviewErrorsPage;
